import { useMemo } from "react";
import { useDisclosure } from "./use-disclosure";
import type { DisclosureTriggerProps } from "./use-disclosure";

export interface OverflowList<T> {
  visible: readonly T[];
  overflowCount: number;
  isExpanded: boolean;
  toggleProps: DisclosureTriggerProps;
  listId: string;
}

/**
 * Show the first `limit` items, keep the rest behind a toggle.
 *
 * `overflowCount` is how many are hidden while COLLAPSED, so the toggle can still say
 * "+ 3 more" / "Show fewer" once expanded.
 */
export function useOverflowList<T>(
  items: readonly T[],
  limit: number
): OverflowList<T> {
  const { isOpen, triggerProps, panelProps } = useDisclosure(false);

  const visible = useMemo(
    () => (isOpen ? items : items.slice(0, limit)),
    [items, limit, isOpen]
  );

  return {
    visible,
    overflowCount: Math.max(0, items.length - limit),
    isExpanded: isOpen,
    toggleProps: triggerProps,
    listId: panelProps.id
  };
}

export default useOverflowList;
